'use client';

import React, { useState, useEffect, useRef } from 'react';

const faqs = [
  {
    q: '¿Qué es exactamente AQUÍ APP?',
    a: 'AQUÍ APP es una plataforma de descubrimiento local que conecta a consumidores venezolanos con negocios cercanos. Puedes buscar productos y servicios, confirmar disponibilidad y contactar al vendedor antes de ir — todo desde tu teléfono.',
  },
  {
    q: '¿Cuándo estará disponible la app?',
    a: 'AQUÍ APP está próximamente disponible para iOS y Android. Puedes registrarte ahora para recibir acceso anticipado y ser de los primeros en usarla cuando lancemos en tu ciudad.',
  },
  {
    q: '¿Cuánto cuesta registrar mi negocio?',
    a: 'El registro durante el período de lanzamiento es completamente gratuito. Tendremos planes premium con beneficios adicionales, pero siempre habrá una opción básica sin costo para que cualquier negocio local pueda estar en la plataforma.',
  },
  {
    q: '¿En qué ciudades de Venezuela estará disponible?',
    a: 'Inicialmente lanzaremos en las principales ciudades: Caracas, Valencia, Maracaibo, Barquisimeto y Maracay. La expansión a otras ciudades dependerá de la demanda y el registro de partners en cada zona.',
  },
  {
    q: '¿Cómo se verifica la disponibilidad de productos?',
    a: 'Los partners (negocios registrados) actualizan su inventario en tiempo real desde la app. Los consumidores pueden confirmar existencias antes de desplazarse, ahorrando tiempo y evitando viajes innecesarios.',
  },
  {
    q: '¿Qué diferencia a AQUÍ APP de otras plataformas?',
    a: 'AQUÍ APP está diseñada específicamente para el mercado venezolano, con un enfoque en la búsqueda local por proximidad. No somos un e-commerce nacional, somos tu directorio local inteligente que te conecta con lo que necesitas en tu zona, en tiempo real.',
  },
];

export default function FAQSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const hiddenEls = entry.target.querySelectorAll('.reveal-hidden');
            hiddenEls.forEach((el, i) => {
              setTimeout(() => el.classList.add('reveal-visible'), i * 80);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-32 relative overflow-hidden">
      <div className="absolute inset-0"
        style={{ background: 'linear-gradient(180deg, transparent 0%, #001070 15%, #001070 85%, transparent 100%)' }}
      />
      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <div className="reveal-hidden">
            <p className="text-[10px] font-black uppercase tracking-[0.5em] text-primary">Preguntas frecuentes</p>
          </div>
          <div className="reveal-hidden">
            <h2 className="text-4xl md:text-6xl font-black tracking-tight">
              Resolvemos tus{' '}
              <span className="gradient-text">dudas</span>
            </h2>
          </div>
        </div>

        {/* FAQ Items */}
        <div className="space-y-3">
          {faqs?.map((faq, i) => (
            <div
              key={i}
              className="reveal-hidden rounded-[20px] border overflow-hidden transition-all duration-300 group"
              style={{
                background: openIdx === i ? '#001A8A' : '#001070',
                borderColor: openIdx === i ? 'rgba(244,141,49,0.25)' : 'rgba(255,255,255,0.06)',
              }}
            >
              <button
                className="w-full flex items-center justify-between p-7 text-left gap-4"
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                aria-expanded={openIdx === i}
              >
                <h3 className="text-base font-bold text-white group-hover:text-white/90 transition-colors pr-4">
                  {faq?.q}
                </h3>
                <div
                  className="flex-shrink-0 w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-300"
                  style={{
                    borderColor: openIdx === i ? '#F48D31' : 'rgba(255,255,255,0.12)',
                    background: openIdx === i ? 'rgba(244,141,49,0.1)' : 'transparent',
                    transform: openIdx === i ? 'rotate(45deg)' : 'rotate(0deg)',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke={openIdx === i ? '#F48D31' : 'rgba(255,255,255,0.5)'}
                    strokeWidth="2.5"
                  >
                    <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                  </svg>
                </div>
              </button>
              {openIdx === i && (
                <div className="px-7 pb-7">
                  <p className="text-muted leading-relaxed">{faq?.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="reveal-hidden mt-16 text-center space-y-4">
          <p className="text-muted">¿Tienes más preguntas? Escríbenos directamente.</p>
          <a
            href="mailto:aki.gts.2024@gmail.com"
            className="inline-flex items-center gap-2 text-sm font-black uppercase tracking-widest text-primary hover:text-primary-light transition-colors"
          >
            Contáctanos
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}